import { App, Modal } from 'antd';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { api, ApiError, errorMessage, unwrap } from '../api/client';
import { FieldWrap } from './FieldWrap';

// Self-service "Set signature PIN" (user block): password + new PIN + confirm → POST /auth/pin/set (ADR-004: the PIN is separate
// from the password and never stored in the client). 422 field errors from the API land under the matching field.
const schema = z
  .object({
    password: z.string().min(1, 'pin.error.password'),
    newPin: z.string().regex(/^\d{4,8}$/, 'pin.error.format'),
    confirmPin: z.string(),
  })
  .refine((v) => v.newPin === v.confirmPin, { path: ['confirmPin'], message: 'pin.error.mismatch' });
type FormValues = z.infer<typeof schema>;

export function SetPinModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { password: '', newPin: '', confirmPin: '' } });

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (v: FormValues) => {
    try {
      await unwrap(api.POST('/api/v1/auth/pin/set', { body: { password: v.password, newPin: v.newPin } }));
      void message.success(t('pin.done'));
      close();
    } catch (e) {
      if (e instanceof ApiError && Object.keys(e.fields).length) {
        for (const [field, msgs] of Object.entries(e.fields)) {
          const name = field === 'password' || field === 'newPin' ? field : 'newPin';
          setError(name, { message: msgs.join(' ') });
        }
      } else void message.error(errorMessage(e, t('pin.error.failed')));
    }
  };

  const err = (key: string | undefined) => (key ? t(key) : undefined);

  return (
    <Modal open={open} width={420} title={t('pin.title')} onCancel={close} okText={t('pin.save')} cancelText={t('action.cancel')} onOk={() => void handleSubmit(submit)()} confirmLoading={isSubmitting} destroyOnHidden>
      <form onSubmit={(e) => void handleSubmit(submit)(e)} noValidate>
        <p className="muted small">{t('pin.explain')}</p>
        <FieldWrap label={t('pin.password')} htmlFor="pin-password" required error={err(errors.password?.message)}>
          <input id="pin-password" type="password" autoComplete="current-password" {...register('password')} />
        </FieldWrap>
        <FieldWrap label={t('pin.new')} htmlFor="pin-new" required error={err(errors.newPin?.message)}>
          <input id="pin-new" type="password" inputMode="numeric" autoComplete="new-password" {...register('newPin')} />
        </FieldWrap>
        <FieldWrap label={t('pin.confirm')} htmlFor="pin-confirm" required error={err(errors.confirmPin?.message)}>
          <input id="pin-confirm" type="password" inputMode="numeric" autoComplete="new-password" {...register('confirmPin')} />
        </FieldWrap>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
